import { WASTE_HALF_YEAR_DAYS, WASTE_YEAR_DAYS } from '@d-orca/shared/inventory';
import type { WasteCycle, WasteProjection } from '@interfaces/ILogistics';
import { formatShortDate, type Lang } from '@utils/dateUtils';

/** A bar per closed cycle, plus one dashed "next" bar at the projected
 *  per-cycle average so the trend has somewhere to point. */
export type WastePerCyclePoint = {
  /** Cycle number, 1-based, oldest first — the x label. */
  cycle: number;
  closedAt: string;
  waste: number | null;
  projected: number | null;
};

/** How far the over-time chart projects: `year` — the 6- and 12-month marks
 *  (the tiles' horizons); `nextCycle` — one average cycle past the last close,
 *  so the axis stays on the history's own scale. */
export type WasteOutlook = 'year' | 'nextCycle';

/** Cumulative loss at each close, then the projection's marks on the same
 *  axis — the 6- and 12-month ones, or the next expected close (`outlook`).
 *  `recorded` and `projected` share the last close so the dashed line
 *  continues the solid one. */
export type WasteOverTimePoint = {
  at: string;
  /** `at` as epoch ms — the x value of a numeric time axis, so the 6- and
   *  12-month marks sit at their true distance, not one slot apart. */
  ms: number;
  recorded: number | null;
  projected: number | null;
};

const DAY_MS = 86_400_000;
const round2 = (n: number): number => Math.round(n * 100) / 100;

export function perCycleSeries(
  history: WasteCycle[],
  projection: WasteProjection | null,
): WastePerCyclePoint[] {
  const points: WastePerCyclePoint[] = history.map((h, i) => ({
    cycle: i + 1,
    closedAt: h.closedAt,
    waste: h.waste,
    projected: null,
  }));
  if (projection) {
    points.push({ cycle: history.length + 1, closedAt: '', waste: null, projected: projection.perCycle });
  }
  return points;
}

export function overTimeSeries(
  history: WasteCycle[],
  projection: WasteProjection | null,
  outlook: WasteOutlook = 'year',
): WasteOverTimePoint[] {
  let running = 0;
  const points: WasteOverTimePoint[] = history.map((h) => {
    running = round2(running + h.waste);
    return { at: h.closedAt, ms: Date.parse(h.closedAt), recorded: running, projected: null };
  });
  const last = points[points.length - 1];
  if (projection && last) {
    // `running` IS the last recorded total — read it directly rather than
    // through the point's nullable field.
    const total = running;
    last.projected = total;
    const mark = (days: number, projectedLoss: number): WasteOverTimePoint => {
      const ms = last.ms + days * DAY_MS;
      return { at: new Date(ms).toISOString(), ms, recorded: null, projected: round2(total + projectedLoss) };
    };
    if (outlook === 'year') {
      points.push(mark(WASTE_HALF_YEAR_DAYS, projection.in6Months), mark(WASTE_YEAR_DAYS, projection.in12Months));
    } else {
      // The next close: an average cycle's length after the last one, at the
      // average waste per cycle.
      const cycleDays =
        history.reduce((sum, cycle) => sum + (Date.parse(cycle.closedAt) - Date.parse(cycle.openedAt)), 0) /
        history.length /
        DAY_MS;
      points.push(mark(cycleDays, projection.perCycle));
    }
  }
  return points;
}

/** An over-time axis tick: the instant's calendar day in the SITE's zone. */
export function wasteTimeTick(ms: number, lang: Lang, tz: string): string {
  return formatShortDate(new Date(ms), lang, tz);
}
